import { Divider,FormControl,Grid,InputLabel,ListSubheader,MenuItem,Select,Stack,TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { NewFormContext } from "../../../context/NewFormContext";
import Checklists from "../../../service/Checklists";
import { ChecklistSubitem,ChecklistType } from "../../../types/checklist/types";
import IconsRender from "../IconsRender";
import { getNumberMask, normalizeToFloat } from "../../../helpers/getNumberMask";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

type props = {
  subitem: ChecklistSubitem;
  itemId: string;
  indexSubitem: number;
};

const checklistService = new Checklists();

export default function ValorMedida({ subitem, itemId, indexSubitem }: props) {
  const { setModelo } = useContext(NewFormContext);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["GET_MEDIDAS"],

    queryFn: async () => {
      const response = await checklistService.getMedidas(null);
      return response;
    },
    retry: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const categorias = {
    Peso: ["grama", "quilo", "tonelada"],
    Tamanho: ["metro", "centimetro", "milimetro", "kilometro", "polegada"],
    Volume: ["litro", "mililitro"],
    Pressão: ["bar", "pascal", "quilopascal", "libra"],
    Moeda: ["real", "dolar", "euro", "libraEsterlina"],
    Outros: ["porcentagem"],
  };

  function handleValue(event: string) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  subtipo: event,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }

  function handlePadrao(event: React.ChangeEvent<HTMLInputElement>) {
    const valor = getNumberMask(event.target.value);
    if (valor === "") {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    padrao: "",
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
      return;
    }
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens?.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  padrao: valor,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }

  function handleMin(value: string) {
    const valor = getNumberMask(value);
    if (valor === "") {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    min: "",
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
      return;
    }
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  min: valor,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }

  function handleMax(value: string) {
    const valor = getNumberMask(value);
    if (valor === "") {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    max: "",
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
      return;
    }
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  max: valor,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }

  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" width="100%">
      <Grid container spacing={1.5} width="100%">
        <Grid size={6}>
          <Stack direction="column" spacing={1.5} alignItems="center">
            <FormControl required fullWidth>
              <InputLabel id="demo-simple-select-label">Tipo</InputLabel>
              <Select
                required
                disabled={isLoading || isFetching}
                value={subitem.subtipo}
                label="Tipo"
                onChange={(event) => handleValue(event.target.value as string)}
                SelectDisplayProps={{
                  "aria-labelledby": "grouped-select-label",
                }}>
                {Object.entries(categorias).flatMap(([categoria, ids]) => {
                  const header = <ListSubheader key={`header-${categoria}`}>{categoria}</ListSubheader>;
                  const items =
                    data?.medidas
                      ?.filter((m: { id: string; simbolo: string; nome: string }) => ids.includes(m.id))
                      ?.map((m: { id: string; simbolo: string; nome: string }) => (
                        <MenuItem key={m.id} value={m.id}>
                          <Stack direction="row" justifyContent="flex-start" alignItems="center">
                            <IconsRender value={m.id} /> | {m.nome}
                          </Stack>
                        </MenuItem>
                      )) ?? [];

                  return [header, ...items];
                })}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={4}>
          <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
              <TextField
                disabled={!subitem.subtipo}
                type="text"
                slotProps={{ htmlInput: { style: { textAlign: "center" } } }}
                value={subitem.min ?? ""}
                label="min"
                variant="standard"
                onChange={(event) => {
                  const value = getNumberMask(event.target.value);
                  handleMin(value);
                }}
              />
              <Divider orientation="vertical" flexItem />
              <TextField
                disabled={!subitem.subtipo}
                type="text"
                value={subitem.max ?? ""}
                slotProps={{ htmlInput: { style: { textAlign: "center" } } }}
                label="max"
                variant="standard"
                onChange={(event) => {
                  const value = getNumberMask(event.target.value);
                  handleMax(value);
                }}
              />
            </Stack>
            {(() => {
              const minVal = normalizeToFloat(subitem.min);
              const maxVal = normalizeToFloat(subitem.max);
              const isMinValid = minVal !== null && minVal !== undefined && !isNaN(minVal);
              const isMaxValid = maxVal !== null && maxVal !== undefined && !isNaN(maxVal);
              return isMinValid && isMaxValid && maxVal < minVal ? (
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                  <ErrorOutlineIcon color="error" />
                  <Typography fontSize={12} fontWeight={700} color="textDisabled">
                    - Valor min não pode ser maior que max
                  </Typography>
                </Stack>
              ) : null;
            })()}
          </Stack>
        </Grid>
        <TextField
          type="text"
          disabled={!subitem.subtipo}
          value={subitem.padrao ?? ""}
          onChange={handlePadrao}
          label="Valor padrão"
        />
      </Grid>
    </Stack>
  );
}
